/*
 * Centro Repuestos - búsqueda predictiva general (tema borrador).
 * Shopify determina los candidatos; esta clase sólo ordena páginas/artículos
 * recibidos según relevancia y limita a 5 páginas relevantes, sin excepciones por modelo.
 * Mantiene los productos y las sugerencias de Shopify.
 */
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.modal = this.closest('.search-modal');
    this.cachedResults = Object.create(null);
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.searchTerm = '';
    this.currentController = null;
    this.requestSequence = 0;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.querySelector('form.search').addEventListener('submit', this.onFormSubmit.bind(this));
    this.querySelector('button[type="button"]').addEventListener('click', this.close.bind(this));
    this.querySelector('button[type="reset"]').addEventListener('click', this.clear.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));

    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.clear();
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onFocus() {
    document.body.classList.add('predictive-search--focus');
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.clear(event);
      this.close(true);
    }
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;
    if (!activeElement) return;
    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;
    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');
    if (selectedProduct) selectedProduct.click();
  }

  // Evita que una respuesta antigua reemplace las sugerencias de la consulta actual.
  cancelPendingRequest() {
    this.requestSequence += 1;
    if (this.currentController) {
      this.currentController.abort();
      this.currentController = null;
    }
    return this.requestSequence;
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.toLocaleLowerCase();
    const currentSequence = this.cancelPendingRequest();
    this.setLiveRegionLoadingState();

    if (Object.prototype.hasOwnProperty.call(this.cachedResults, queryKey)) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      if (this.modal) this.modal.classList.add('searching');
      return;
    }

    // Shopify permite hasta 10 resultados por tipo. Recuperamos candidatos
    // adicionales antes de ordenar, sin cambiar el número de fichas públicas.
    const parameters = new URLSearchParams({
      q: searchTerm,
      'resources[limit]': '10',
      'resources[limit_scope]': 'each',
      'resources[options][fields]': 'title,body',
      section_id: 'predictive-search'
    });

    const controller = new AbortController();
    this.currentController = controller;

    fetch(`${theme.routes.predictive_search_url}?${parameters.toString()}`, {
      signal: controller.signal
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Búsqueda predictiva: HTTP ${response.status}`);
        return response.text();
      })
      .then((text) => {
        if (currentSequence !== this.requestSequence || searchTerm !== this.getQuery()) return;

        const fragment = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');
        if (!fragment) throw new Error('Shopify no devolvió la sección predictiva.');
        const resultsMarkup = fragment.innerHTML;

        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
        if (this.modal) this.modal.classList.add('searching');
      })
      .catch((error) => {
        if (error.name === 'AbortError' || currentSequence !== this.requestSequence) return;
        console.warn('No se pudo recuperar la búsqueda predictiva:', error);
        this.close();
      })
      .finally(() => {
        if (this.currentController === controller) this.currentController = null;
      });
  }

  // Normaliza acentos, mayúsculas y puntuación, sin vocabularios por marca/modelo.
  normalizeText(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  // Ignora los sufijos editoriales después de "|" (por ejemplo, el nombre del sitio).
  // Así "Contacto | Centro Repuestos Land Rover" no gana relevancia por "Rover".
  normalizePageTitle(title) {
    const mainTitle = String(title || '').split('|')[0];
    return this.normalizeText(mainTitle)
      .replace(/^repuestos(?: (?:de|para))? /, '')
      .replace(/ en chile$/, '')
      .trim();
  }

  // Solo normalización lingüística sencilla. No hay listas de marcas/modelos.
  getSignificantWords(phrase) {
    const common = new Set(['de', 'del', 'la', 'el', 'los', 'las', 'y', 'en', 'para', 'por', 'repuestos']);
    return phrase.split(' ').filter((word) => word && !common.has(word));
  }

  // Admite singular/plural simple para «filtro/filtros» o «sensor/sensores».
  wordMatches(queryWord, titleWord) {
    if (queryWord === titleWord) return true;
    if (queryWord.length < 4 || titleWord.length < 4) return false;
    const root = (word) => word.replace(/(?:es|s)$/, '');
    return root(queryWord) === root(titleWord);
  }

  rankPage(item, normalizedQuery, originalPosition) {
    const visibleTitle = item.dataset.crPageTitle ||
      item.querySelector('.predictive-search__item-heading')?.textContent || '';
    const title = this.normalizePageTitle(visibleTitle);
    const query = normalizedQuery;
    const bodyHit = item.dataset.crBodyHit === 'true';
    if (!title || !query) return { score: 0, titleHit: false, bodyHit: false };

    const titleWords = title.split(' ');
    const queryWords = this.getSignificantWords(query);
    const titleSignificantWords = this.getSignificantWords(title);
    const extraWords = Math.max(0, titleWords.length - query.split(' ').length);
    const phrase = ` ${query} `;
    const wrappedTitle = ` ${title} `;

    let score = 0;
    let titleHit = false;

    if (title === query) {
      score = 1000;
      titleHit = true;
    } else if (wrappedTitle.includes(phrase)) {
      if (title.endsWith(` ${query}`)) score = 880;
      else if (title.startsWith(`${query} `)) score = 850;
      else score = 820;
      score -= extraWords * 15;
      titleHit = true;
    } else if (title.startsWith(query)) {
      score = 740 - extraWords * 10;
      titleHit = true;
    } else if (query.length >= 3 && title.includes(query)) {
      score = 670 - extraWords * 10;
      titleHit = true;
    } else if (queryWords.length) {
      const matchingWords = queryWords.filter((queryWord) =>
        titleSignificantWords.some((titleWord) => this.wordMatches(queryWord, titleWord))
      ).length;
      if (matchingWords) {
        const coverage = matchingWords / queryWords.length;
        score = 480 + Math.round(coverage * 170) - extraWords * 5;
        titleHit = true;
      }
    }

    if (!titleHit && bodyHit) {
      // Coincidencia real en el cuerpo, útil para «Vogue» o «Freelander 2».
      score = 420;
    }
    if (titleHit && bodyHit) score += 15;

    // El orden original de Shopify desempata sin alterar la relevancia principal.
    return { score: score - originalPosition * 0.001, titleHit, bodyHit };
  }

  // Hasta 5 páginas relevantes; no filtra productos, artículos ni sugerencias.
  // Se aplica por separado a las listas HTML de escritorio y móvil.
  sortPageSuggestions() {
    const normalizedQuery = this.normalizeText(this.getQuery());
    if (!normalizedQuery) return;

    const maxPages = 5;
    const queryWordCount = this.getSignificantWords(normalizedQuery).length;
    this.predictiveSearchResults
      .querySelectorAll('#predictive-search-results-pages-list-desktop, #predictive-search-results-pages-list-mobile')
      .forEach((list) => {
        const pages = Array.from(list.children).filter((item) =>
          item.matches('li[data-cr-page-title]')
        );
        const ranked = pages.map((item, index) => ({
          item, originalPosition: index,
          ...this.rankPage(item, normalizedQuery, index)
        }));
        const hasTitleHit = ranked.some((candidate) => candidate.titleHit);
        const relevant = ranked
          .filter((candidate) => {
            if (candidate.score < 400) return false;
            // Para consultas con varios términos (p. ej. «Freelander 2»),
            // prioriza el título parcialmente coincidente sobre páginas
            // cuyo único indicio es una mención secundaria en el contenido.
            if (queryWordCount > 1 && hasTitleHit && !candidate.titleHit) return false;
            return true;
          })
          .sort((a, b) => b.score - a.score || a.originalPosition - b.originalPosition)
          .slice(0, maxPages);

        const keep = new Set(relevant.map((candidate) => candidate.item));
        pages.forEach((page) => { if (!keep.has(page)) page.remove(); });

        // Las páginas quedan primero; los artículos existentes se conservan.
        const fragment = document.createDocumentFragment();
        relevant.forEach((candidate) => fragment.appendChild(candidate.item));
        list.prepend(fragment);

        const wrapper = list.closest('.predictive-search__pages-wrapper');
        if (wrapper) wrapper.hidden = list.querySelectorAll('li').length === 0;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.sortPageSuggestions();
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const container = this.predictiveSearchResults;
    const pages = container.querySelectorAll('#predictive-search-results-pages-list-desktop li').length;
    const products = container.querySelectorAll('#predictive-search-results-products-list li').length;
    const suggestions = container.querySelectorAll('#predictive-search-results-queries-list li').length;
    const total = pages + products + suggestions;
    this.setLiveRegionText(`${total} resultados sugeridos`);
  }

  getResultsMaxHeight() {
    const header = document.querySelector('.header');
    this.resultsMaxHeight = header ? window.innerHeight - header.getBoundingClientRect().bottom : window.innerHeight;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    document.body.classList.add('predictive-search--focus');
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.cancelPendingRequest();
      this.input.value = '';
      this.searchTerm = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    document.body.classList.remove('predictive-search--focus');
  }

  clear(event) {
    if (event) event.preventDefault();
    this.cancelPendingRequest();
    this.input.value = '';
    this.searchTerm = '';
    this.removeAttribute('open');
    this.removeAttribute('results');
    this.input.focus();
    if (this.modal) this.modal.classList.remove('searching');
  }
}

customElements.define('predictive-search', PredictiveSearch);
